import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { PERMISSION_DEFINITIONS } from '@fintree/supabase/roles'
import type { CustomRole, Permission } from '@fintree/supabase/roles'
import type { ActivityActor } from '@fintree/supabase/shared'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useConfirm } from '@/hooks/useConfirm'
import { GROUP_ORDER, roleFormSchema, type RoleFormValues } from './settingsTypes'
import {
  useCreateCustomRole,
  useDeleteCustomRole,
  useUpdateCustomRole,
} from '@/hooks/mutations/useStaffRoleMutations'
import { useCustomRoles, useStaffMembers } from '@/hooks/queries/useStaffQueries'

interface RolesTabProps {
  actor: ActivityActor
}

export function RolesTab({ actor }: RolesTabProps) {
  const { confirm, confirmDialogProps } = useConfirm()
  const [roleDialog, setRoleDialog] = useState<{ mode: 'create' } | { mode: 'edit'; role: CustomRole } | null>(null)

  const { data: roles = [], isLoading: rolesLoading, isError: rolesError } = useCustomRoles()
  const staffQuery = useStaffMembers()

  const staffByRoleId = useMemo(() => {
    const map = new Map<string, number>()
    for (const row of staffQuery.data ?? []) {
      if (!row.custom_role_id) continue
      map.set(row.custom_role_id, (map.get(row.custom_role_id) ?? 0) + 1)
    }
    return map
  }, [staffQuery.data])

  const roleForm = useForm<RoleFormValues>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: { name: '', description: '', permissions: [] },
  })

  const createRoleMut = useCreateCustomRole(actor, {
    onSuccess: () => {
      setRoleDialog(null)
      roleForm.reset()
    },
  })

  const updateRoleMut = useUpdateCustomRole(actor, {
    onSuccess: () => {
      setRoleDialog(null)
      roleForm.reset()
    },
  })

  const deleteRoleMut = useDeleteCustomRole(actor)

  function openCreateRole() {
    roleForm.reset({ name: '', description: '', permissions: [] })
    setRoleDialog({ mode: 'create' })
  }

  function openEditRole(role: CustomRole) {
    roleForm.reset({
      name: role.name,
      description: role.description ?? '',
      permissions: [...role.permissions],
    })
    setRoleDialog({ mode: 'edit', role })
  }

  function togglePermission(key: string, checked: boolean) {
    const cur = roleForm.getValues('permissions')
    if (checked) roleForm.setValue('permissions', [...new Set([...cur, key])])
    else roleForm.setValue(
      'permissions',
      cur.filter((k) => k !== key)
    )
  }

  const definitionsByGroup = useMemo(() => {
    const m = new Map<string, (typeof PERMISSION_DEFINITIONS)[number][]>()
    for (const def of PERMISSION_DEFINITIONS) {
      const list = m.get(def.group) ?? []
      m.set(def.group, [...list, def])
    }
    return m
  }, [])

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Roles" subtitle="Named roles and permission bundles." />
        <Button type="button" onClick={openCreateRole}>
          + Create role
        </Button>
      </div>

      {rolesLoading ? (
        <LoadingSpinner />
      ) : rolesError ? (
        <div className="rounded-lg border border-destructive/ bg-destructive/ p-4 text-sm text-destructive">Unable to load roles.</div>
      ) : roles.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">No roles found.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Role name</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Permissions</th>
                <th className="px-3 py-2">Staff</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-foreground">{row.name}</td>
                  <td className="max-w-xs px-3 py-2 text-muted-foreground">{row.description ?? '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {row.permissions.slice(0, 6).map((p) => (
                        <span key={p} className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium">
                          {PERMISSION_DEFINITIONS.find((d) => d.key === p)?.label ?? p}
                        </span>
                      ))}
                      {row.permissions.length > 6 ? (
                        <span className="text-[10px] text-muted-foreground">+{row.permissions.length - 6}</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{staffByRoleId.get(row.id) ?? 0}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => openEditRole(row)}>
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={row.name === 'super_admin'}
                        onClick={() =>
                          void (async () => {
                            const ok = await confirm({
                              title: 'Delete role?',
                              description: 'This cannot be undone if no staff are assigned to this role.',
                              confirmLabel: 'Delete',
                              cancelLabel: 'Cancel',
                              variant: 'danger',
                            })
                            if (ok) deleteRoleMut.mutate(row.id)
                          })()
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={Boolean(roleDialog)}
        onOpenChange={(o) => {
          if (!o) setRoleDialog(null)
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{roleDialog?.mode === 'edit' ? 'Edit role' : 'Create role'}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4 py-2"
            onSubmit={roleForm.handleSubmit((values) => {
              const payload = {
                name: values.name,
                description: values.description ?? '',
                permissions: values.permissions as Permission[],
              }
              if (roleDialog?.mode === 'edit') updateRoleMut.mutate({ id: roleDialog.role.id, values: payload })
              else createRoleMut.mutate(payload)
            })}
          >
            <div>
              <Label>Role name</Label>
              <Input className="mt-1" disabled={roleDialog?.mode === 'edit' && roleDialog.role.name === 'super_admin'} {...roleForm.register('name')} />
              {roleForm.formState.errors.name ? (
                <p className="mt-1 text-xs text-destructive">{roleForm.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div>
              <Label>Description</Label>
              <Input className="mt-1" {...roleForm.register('description')} />
            </div>
            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">Permissions</p>
              {GROUP_ORDER.map((group) => {
                const defs = definitionsByGroup.get(group)
                if (!defs?.length) return null
                return (
                  <div key={group}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</p>
                    <div className="space-y-2">
                      {defs.map((def) => {
                        // RHF watch() is intentional here; React Compiler flags it as incompatible with memoization.
                        // eslint-disable-next-line react-hooks/incompatible-library -- false positive: permission toggles need live form state
                        const checked = roleForm.watch('permissions').includes(def.key)
                        return (
                          <label key={def.key} className="flex cursor-pointer items-start gap-2 text-sm">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => togglePermission(def.key, v === true)}
                              className="mt-0.5"
                            />
                            <span>
                              <span className="font-medium text-foreground">{def.label}</span>
                              <span className="text-muted-foreground"> — {def.description}</span>
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setRoleDialog(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createRoleMut.isPending || updateRoleMut.isPending}
              >
                {roleDialog?.mode === 'edit' ? (updateRoleMut.isPending ? 'Saving…' : 'Save') : createRoleMut.isPending ? 'Creating…' : 'Create role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {confirmDialogProps ? (
        <ConfirmDialog
          open={confirmDialogProps.open}
          title={confirmDialogProps.title}
          description={confirmDialogProps.description}
          confirmLabel={confirmDialogProps.confirmLabel}
          cancelLabel={confirmDialogProps.cancelLabel}
          variant={confirmDialogProps.variant}
          onConfirm={confirmDialogProps.onConfirm}
          onCancel={confirmDialogProps.onCancel}
        />
      ) : null}
    </section>
  )
}
